import { z } from 'zod';

import { CustomsLogType, DeclarationCustomsFlowType, NCTSMessageType, TransitErrorType } from '@e-origin/shared';

import { TransitHouseConsignmentSchema } from './TransitConsignmentSchema';
import {
  optionalBoolean,
  optionalDate,
  optionalNumber,
  optionalString,
  TransitAddressSchema,
  TransitCustomsStateNotificationSchema,
  TransitLocationOfGoodsSchema,
  TransitTransportEquipmentSchema,
} from './utils';

export const TransitControlResultSchema = z.object({
  code: optionalString(),
  date: optionalString(),
  remarks: optionalString(),
  controlledBy: optionalString(),
  text: optionalString(),
});

export const TransitCustomsDeclarationRejectionSchema = z.object({
  businessRejectionType: optionalString(),
  rejectionCode: optionalString(),
  rejectionDate: optionalString(),
  rejectionReason: optionalString(),
});

export const TransitCustomsLogSchema = z.object({
  customsMessageCode: optionalString(),
  parsedAt: optionalDate(),
  issuedAt: optionalDate(),
  log: optionalString(),
  logFile: optionalString(),
  type: z.nativeEnum(CustomsLogType).optional().nullable(),
});

export const TransitFunctionalErrorSchema = z.object({
  errorCode: optionalString(),
  errorPointer: optionalString(),
  errorReason: optionalString(),
  errorType: z.nativeEnum(TransitErrorType).optional().nullable(),
  remarks: optionalString(),
  sequenceNumber: optionalString(),
  originalAttributeValue: optionalString(),
});

export const TransitInvalidationRequestSchema = z.object({
  invalidationReason: optionalString(),
  invalidationRequestDate: optionalDate(),
  correlationId: optionalString(),
});

export const TransitInvalidationDecisionSchema = z.object({
  invalidationDecisionDate: optionalDate(),
  invalidationRequestDate: optionalDate(),
  decision: optionalString(),
  invalidationInitiatedByCustoms: optionalString(),
  invalidationReason: optionalString(),
  correlationId: optionalString(),
});

export const TransitReleaseSchema = z.object({ dateOfRelease: optionalString() });

export const TransitNoReleaseSchema = z.object({
  rejectionDate: optionalString(),
  noReleaseMotivationCode: optionalString(),
  noReleaseMotivationText: optionalString(),
});

export const TransitWriteOffSchema = z.object({ writeOffDate: optionalString() });

export const TransitReleaseDestinationSchema = z.object({
  releaseDate: optionalString(),
  releaseIndicator: optionalString(),
  sequenceNumber: optionalString(),
  HouseConsignment: z.array(TransitHouseConsignmentSchema).optional(),
});

export const InvalidGuaranteeReasonSchema = z.object({
  sequenceNumber: optionalString(),
  code: optionalString(),
  text: optionalString(),
});

export const INVGuaranteeReferenceSchema = z.object({
  sequenceNumber: optionalString(),
  GRN: optionalString(),
  InvalidGuaranteeReason: z.array(InvalidGuaranteeReasonSchema).optional(),
});

export const UnloadPermissionSchema = z.object({
  unloadPermissionDate: optionalString(),
  continueUnloading: optionalString(),
  stateOfSealsRequired: optionalBoolean(),
});

export const TransitTypeOfControlsSchema = z.object({ type: optionalString(), remarks: optionalString() });

export const TransitRequestedDocumentsSchema = z.object({
  type: optionalString(),
  ccQualifier: optionalString(),
  referenceNumber: optionalString(),
  description: optionalString(),
});

export const TransitInControlSchema = z.object({
  notificationDate: optionalString(),
  controlType: optionalString(),
  customsRegistrationNumber: optionalString(),
  notificationType: optionalString(),
  anticipatedControlDate: optionalString(),
  text: optionalString(),
  typeOfControls: z.array(TransitTypeOfControlsSchema).optional(),
  requestedDocuments: z.array(TransitRequestedDocumentsSchema).optional(),
});

export const DiscrepancySchema = z.object({ date: optionalDate(), text: optionalString() });

export const RequestNonArrivedSchema = z.object({
  requestDate: optionalDate(),
  limitForResponseDate: optionalDate(),
  customsOfficeOfEnquiryAtDeparture: z.object({ referenceNumber: optionalString() }).optional(),
});

export const EndorsementSchema = z.object({
  date: optionalString(),
  authority: optionalString(),
  place: optionalString(),
  country: optionalString(),
});

export const TransportMeansSchema = z.object({
  typeOfIdentification: optionalString(),
  identificationNumber: optionalString(),
  nationality: optionalString(),
});

export const TranshipmentSchema = z.object({
  containerIndicator: optionalString(),
  TransportMeans: TransportMeansSchema.optional(),
});

export const IncidentSchema = z.object({
  id: optionalNumber(),
  sequenceNumber: optionalString(),
  code: optionalString(),
  text: optionalString(),
  incidentNotificationDateAndTime: optionalDate(),
  officeOfIncidentRegistrationReference: optionalString(),
  Endorsement: EndorsementSchema.optional(),
  Location: TransitLocationOfGoodsSchema.optional(),
  TransportEquipment: z.array(TransitTransportEquipmentSchema).optional(),
  Transhipment: TranshipmentSchema.optional(),
});

export const RecoveryNotificationSchema = z.object({
  recoveryNotificationDate: optionalDate(),
  recoveryNotificationText: optionalString(),
  amountClaimed: optionalNumber(),
  currency: optionalString(),
});

export const GuarantorSchema = z.object({
  identificationNumber: optionalString(),
  name: optionalString(),
  address: TransitAddressSchema.optional(),
});

export const TransitCustomsStateSchema = z.object({
  controlResult: TransitControlResultSchema.optional(),
  correlationId: optionalString(),
  customsDeclarationRejection: TransitCustomsDeclarationRejectionSchema.optional(),
  customsLogs: z.array(TransitCustomsLogSchema).optional(),
  dateOfAcceptance: optionalDate(),
  arrivalDateAndTimeActual: optionalDate(),
  functionalError: z.array(TransitFunctionalErrorSchema).optional(),
  invalidationRequest: TransitInvalidationRequestSchema.optional(),
  invalidationDecision: TransitInvalidationDecisionSchema.optional(),
  release: TransitReleaseSchema.optional(),
  noRelease: TransitNoReleaseSchema.optional(),
  flow: z.nativeEnum(DeclarationCustomsFlowType).optional().nullable(),
  sentTo: optionalString(),
  writeOff: TransitWriteOffSchema.optional(),
  releaseDestination: TransitReleaseDestinationSchema.optional(),
  INVGuaranteeReference: z.array(INVGuaranteeReferenceSchema).optional(),
  unloadPermission: UnloadPermissionSchema.optional(),
  inControl: TransitInControlSchema.optional(),
  discrepancies: z.array(DiscrepancySchema).optional(),
  requestNonArrived: RequestNonArrivedSchema.optional(),
  lastSentMessageCode: z.nativeEnum(NCTSMessageType).optional().nullable(),
  Incident: z.array(IncidentSchema).optional(),
  recoveryNotification: RecoveryNotificationSchema.optional(),
  guarantor: GuarantorSchema.optional(),
  notifications: z.array(TransitCustomsStateNotificationSchema).optional(),
});
export type TransitCustomsState = z.infer<typeof TransitCustomsStateSchema>;
